import Icon from "lib/components/Icons"
import FormField, { fieldControlA11yProps } from "lib/components/Form/FormField"
import { fieldBox, fieldControl, resolveFieldStatus } from "lib/components/Form/fieldStyles"
import { FormEvent, ReactNode, forwardRef, useId } from "react"

export interface InputProps {
  label?: string
  placeholder?: string
  onChange?: (object) => void
  onBlur?: (object) => void
  onFocus?: (object) => void
  onKeyDown?: (object) => void
  value?: string | number
  name?: string
  disabled?: boolean
  maxLength?: number
  success?: boolean
  error?: boolean
  message?: string | string[]
  /** Neutral hint under the box; replaced by `message` while `error` is set. */
  helperText?: ReactNode
  labelTooltips?: React.ReactNode
  type: "text" | "number" | "email" | "tel" | "password"
  defaultValue?: string
  noBorder?: boolean
  /** Font size of the value; the box stays 36px. */
  size?: "small" | "medium" | "large"
  maxNumber?: number
  onInvalid?: (event: FormEvent<HTMLInputElement>) => void
  /** Merged onto the `<input>` itself, which is where the border lives. */
  customClass?: string
  autoComplete?: "off" | "on" | "new-password" | "email" | "username"
  isBorderBottom?: boolean
  withEmailSuffix?: boolean
  emailSuffix?: string
  placeholderCustomClass?: string
  /** Icon rendered inside the box against its right edge; replaces the status icon when set. */
  rightIcon?: ReactNode
}

const FONT_SIZE_BY_SIZE = { small: "xs", medium: "sm", large: "lg" } as const

function resolveBorder(noBorder: boolean, isBorderBottom: boolean): "none" | "bottom" | "full" {
  if (noBorder) return "none"
  if (isBorderBottom) return "bottom"
  return "full"
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      placeholder,
      onChange,
      value,
      name,
      labelTooltips,
      type,
      disabled = false,
      maxLength,
      success,
      error,
      message,
      helperText,
      maxNumber,
      defaultValue,
      noBorder = false,
      size = "medium",
      onFocus,
      onBlur,
      onInvalid,
      onKeyDown,
      autoComplete = "off",
      isBorderBottom = false,
      customClass = "",
      withEmailSuffix = false,
      emailSuffix = "",
      placeholderCustomClass = "placeholder:text-sm",
      rightIcon,
    }: InputProps,
    inputRef,
  ) => {
    const controlId = useId()
    const status = resolveFieldStatus(error, success)
    const helper = status === "danger" && message ? message : helperText

    let statusIcon: ReactNode = null
    if (!rightIcon && status === "success") {
      statusIcon = <Icon iconName="check" width={20} height={20} color="green" />
    } else if (!rightIcon && status === "danger") {
      statusIcon = <Icon iconName="error" width={20} height={20} color="red" />
    }
    const trailing = rightIcon ?? statusIcon
    const hasAdornment = Boolean(trailing) || withEmailSuffix

    return (
      <FormField
        controlId={controlId}
        label={label}
        labelTooltips={labelTooltips}
        name={name}
        status={status}
        disabled={disabled}
        helperText={helperText}
        message={message}>
        <div className="relative flex-1">
          <input
            className={`${fieldBox({
              status,
              disabled,
              border: resolveBorder(noBorder, isBorderBottom),
              radius: isBorderBottom ? "none" : "md",
              focusMode: "self",
            })} ${fieldControl({
              disabled,
              padding: hasAdornment ? "trailingIcon" : "default",
              fontSize: FONT_SIZE_BY_SIZE[size],
            })} ${placeholderCustomClass} ${customClass}`}
            placeholder={placeholder}
            onChange={onChange}
            onInvalid={onInvalid}
            value={value}
            name={name}
            disabled={disabled}
            type={type}
            maxLength={maxLength}
            ref={inputRef}
            defaultValue={defaultValue}
            max={maxNumber}
            // Only a number input changes value on scroll; blurring text fields would interrupt typing.
            onWheel={type === "number" ? (event) => event.currentTarget.blur() : undefined}
            autoComplete={autoComplete}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            {...fieldControlA11yProps({ controlId, status, hasHelper: Boolean(helper) })}
          />

          {withEmailSuffix && (
            <span className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-neutral-50 ${disabled ? "bg-neutral-30" : "bg-white"}`}>
              {emailSuffix}
            </span>
          )}

          {trailing && !withEmailSuffix && (
            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
              {trailing}
            </span>
          )}
        </div>
      </FormField>
    )
  },
)

Input.displayName = "Input"

export default Input
